<script lang="ts">
	import type { Cache } from "$lib/serv/core/cache";
	import type { Menu, PartRestaurant, RestaurantExposed } from "$lib/serv/restaurant";
	import type { Address } from "proto/address";
	import type { PageServerData } from "./$types";
	
	import { browser } from "$app/environment";
	import { page } from "$app/stores";
	import { profile$ } from "$lib/serv/core/auth";
	import { liCache } from "$lib/serv/core/conf";
	import { createBaseLandingItemsStream, KEY_FOOD_ITEM, KEY_ITEM_MERCH } from "$lib/serv/food-landing";
	import { browsingLocation$, LOCATION_PROPS, streamOptimisticLocation } from "$lib/serv/locations";
	import { buildLDJSON, getMenu, normalise, storeRestaurantExposed } from "$lib/serv/restaurant";
	import { DIVIDER } from "$lib/ui/home/food/menu/server";
	import { KEY_SECTION } from "$lib/ui/layout/SectionedListView.svelte";
	import { selectAddress } from "$lib/ui/modals/AddressModal.svelte";
	import { hasGeo } from "$lib/util/address";
	import { objectPropsEqual } from "$lib/util/object";
	import { fromStore } from "$lib/util/obs";
	import { query, queryFrom } from "$lib/util/url";
	import { combineLatest, distinctUntilChanged, distinctUntilKeyChanged, map, merge, Observable, of, shareReplay, startWith, switchMap, type ObservedValueOf } from "rxjs";

	import Restaurant from "$lib/ui/home/food/menu/Restaurant.svelte";
	import SeoFooter from "$lib/ui/home/seo/SeoFooter.svelte";
	import RestaurantPDP from '$lib/ui/modals/RestaurantPdpModal.svelte';
	import Meta from "$lib/ui/shared/seo/Meta.svelte";
	import SeoBrandsLane from "$lib/ui/shared/seo/SeoBrandsLane.svelte";
	import SeoTagsLane from "$lib/ui/shared/seo/SeoTagsLane.svelte";
import SeoExtraCopy from "$lib/ui/shared/seo/SeoExtraCopy.svelte";

	export let data: PageServerData;

	const page$ = fromStore(page);

	const id$ = page$.pipe(
		map(p => p.params.id),
		distinctUntilChanged(),
	);

	const optionalMenuId$ = page$.pipe(
		map(p => p.url.searchParams.get('menuId')),
		distinctUntilChanged(),
	);

	const params$ = page$.pipe(map(p => p.url.searchParams));
	const foodItem$ = params$.pipe(
		map(p => p.get(KEY_FOOD_ITEM)),
		distinctUntilChanged(),
		map(val => val?.split(DIVIDER) ?? []),
	);
	
	const backHref$ = streamOptimisticLocation(page$.pipe(map(p => p.route.id)), profile$).pipe(
		map(loc => queryFrom(loc as Record<string, string>)),
		map(q => q.keep(...LOCATION_PROPS, 'vertical', KEY_ITEM_MERCH)),
		map(q => q.toUrl('/')),
	);
	const location$ = browsingLocation$.pipe(
		map(loc => queryFrom(loc as Record<string, any>).keep(...LOCATION_PROPS, 'vertical').toObject()),
		distinctUntilChanged(objectPropsEqual(...LOCATION_PROPS, "vertical")),
	);

	const rest$ = id$.pipe(
		switchMap(id => location$.pipe(map(loc => [id, loc] as [string, Partial<Address>]))),
		switchMap(([id, loc]) => obsRestaurant(+id, loc, data.exposed)),
		startWith({ loading: true, payload: data.exposed } as ObservedValueOf<ReturnType<typeof obsRestaurant>>),
		shareReplay(1),
	)

	function obsRestaurant(id: number, loc: Partial<Address>, exposed?: RestaurantExposed) {
		const exp = { payload: (exposed ?? {id}) as PartRestaurant, outOfBounds: true };
		// on the server exposure data is all we'll ever have, just return it
		if (!browser) return of(exp); // SSR data

		if (!id || isNaN(id)) {
			console.error('Invalid restaurant id.');
			return of(null); // invalid id
		}

		if (!hasGeo(loc)) {
			// if we have no location, again exposure data is all we'll ever have, just return it, but save it first
			if (exposed) {
				storeRestaurantExposed(exposed); // save for later
				return of(exp);
			}
			
			// if we have no location and no exposure data, we can't do anything
			return of(null);
		}

		// now that we have a location we'll probably find better data than exposed. we'll save it any way for later just in case
		if (exposed) storeRestaurantExposed(exposed);

		// request geo-based landing items
		return liCache.value$.pipe(
			switchMap(useCache => {
				// let's not be specific about the LI vertical as we do not know which vertical
				// the entity is at this stage and we risk loading SS and Dyn data which excludes
				// this entity resuling in the "out of bounds" notice
				// UPDATE: not spcifying vertical will result in getting restaurants. We need to
				// specify vertical to get the correct data
				// const location$ = of({...loc, vertical:undefined});
				const location$ = of(loc);
				return createBaseLandingItemsStream(location$, false, true, useCache)
			}),
			map(([base, semiStatic, dynamic, , deals]) => {
				const rest = semiStatic?.payload?.semi_static.find(r => r.id === id);
				if (!rest) return {
					payload: (exposed ?? { id }) as PartRestaurant,
					loading: base?.loading || semiStatic?.loading,
					error: semiStatic?.error ?? dynamic?.error,
					outOfBounds: true,
				};
				const dyn = dynamic?.payload?.dynamic.find(r => r.id === id);
				return {
					payload: (dyn ? Object.assign(rest, dyn) : rest) as PartRestaurant,
					loading: semiStatic?.loading ?? dynamic?.loading,
					error: semiStatic?.error ?? dynamic?.error,
					deals: deals?.restaurants.get(id),
				};
			}),
		);
	}

	const menuId$ = combineLatest([rest$,optionalMenuId$]).pipe(
		map(([rest,id]) => +(id??'') || (rest?.payload && 'menu_id' in rest?.payload && rest?.payload?.menu_id) || null),
		startWith(null),
		distinctUntilChanged(),
	);

	const menu$: Observable<Cache<Menu>> = combineLatest([menuId$, foodItem$, page$]).pipe(
		map(([pageMenuId, [itemId, sectionId, menuId], page]) => ({
			isSlug: typeof itemId !== 'undefined' && isNaN(+itemId),
			slug: itemId,
			// sectionId,
			// `pageMenuId` and `menuId` may differ. We'll let `menuId` take precedence.
			menuId: +menuId || pageMenuId,
			ignoreCache: page.url.searchParams.has('fresh'),
		})),
		map(p => ({ ...p, hash: `${p.menuId}:${p.isSlug && p.slug}`})),
		distinctUntilKeyChanged('hash'), // let's make sure we don't trigger a new request if the hash is the same
		switchMap(({ isSlug, slug, menuId, ignoreCache }) => {
			return !menuId ? of({ payload: undefined }) : merge(
				getMenu(menuId, isSlug ? slug : undefined, ignoreCache).pipe(
					map(m => ({ payload: normalise(m) ?? undefined })),
				),
				of({ loading: true }),
			);
		}),
		shareReplay(1), // PDP subscribes very late. let's reuse the last value
	)

</script>

<svelte:head>
	{#if data.exposed}
		{@const rest = data.exposed}
		{@const ldjson = buildLDJSON(data.exposed, '')}
		{#if ldjson}
			{@html `<script type='application/ld+json'>${ldjson}</script>`}
		{/if}
		<Meta meta={data.meta} />
		{#if data.canonical}
			<link rel="canonical" href={data.canonical} />
		{/if}
	{/if}
</svelte:head>

{#if $rest$}
	{@const rest = $rest$}
	{@const {payload:restaurant, loading, deals} = { loading:false, deals:undefined, ...rest}}
	<Restaurant
		{restaurant}
		{deals}
		{menu$}
		{loading}
		sectionFocus={$page.url.searchParams.get(KEY_SECTION)}
		location={$location$}
		backHref={$backHref$}
		page={$page} />
{/if}

{#if query($page.url).has(KEY_FOOD_ITEM)}
	{@const menuItemHash = $page.url.searchParams.get(KEY_FOOD_ITEM) ?? ''}
	{@const idx = $page.url.searchParams.get('edit') ?? undefined}
	
	<RestaurantPDP
		menu={$menu$?.payload}
		{menuItemHash}
		editIndex={typeof idx === 'string' && !isNaN(+idx) ? +idx : undefined}
		currentUrl={$page.url}/>
{/if}

{#if !data.auth && (!$page.url.searchParams.has('latitude') || !$page.url.searchParams.has('longitude'))}
	{#if data.brand}
		{@const {vertical, slug, name} = data.brand}
		<div class="label-xs book">
			<a href={`/delivery/${vertical ?? 'restaurant'}s/${slug}-near-me`}>View the {name} brand page.</a>
		</div>
	{/if}
	
	<!-- Tags: Food -->
	<SeoTagsLane title="Popular food types" list={data.cuisines} />

	<!-- Brands -->
	<SeoBrandsLane title="Popular restaurants" list={data.brands} />
	
	<!-- Tags: Stores -->
	<SeoTagsLane title="Shop by department" list={data.departments} />
	
	<!-- Extra SEO Copy-->
	<SeoExtraCopy seoExtras={data.extra} page={$page}/>

	<!-- Footer area -->
	<SeoFooter appstores={data.apps.stores} socials={data.social} tallogo={data.tallogo}/>
{/if}
